import React, { useEffect, useState } from "react";
import { Head } from "zudoku/components";
import { useTenantStore } from "../stores/tenantStore";
import useGetLogs from "./useGetLogs";

export default function APILogsPage() {
  const { currentTenant } = useTenantStore();
  const { data: logs, isFetching, error } = useGetLogs();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortColumn, setSortColumn] = useState<string>("timestamp");
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    setSearchTerm(""); 
    setCurrentPage(1);
    setExpandedId(null);
  }, [currentTenant]);

  const filteredLogs = logs?.filter((log) => {
    const searchString = `
      ${log.id ?? ""}
      ${log.method ?? ""}
      ${log.url ?? ""}
      ${log.result ?? ""}
      ${log.timestamp ?? ""}
      ${log.errorResponseBody ?? ""}
      ${log.ipAddress ?? ""}
      ${log.appRegistrationId ?? ""}
    `.toLowerCase();

    return searchString.includes(searchTerm.toLowerCase());
  });

  const sortedLogs = filteredLogs?.sort((a, b) => {
    const valueA = a[sortColumn];
    const valueB = b[sortColumn];

    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return sortOrder === "asc" ? -1 : 1;
    if (valueB == null) return sortOrder === "asc" ? 1 : -1;

    if (sortColumn === "timestamp") {
      const dateA = new Date(valueA).getTime();
      const dateB = new Date(valueB).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    }

    if (typeof valueA === "string" && typeof valueB === "string") {
      return sortOrder === "asc"
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    }

    if (typeof valueA === "number" && typeof valueB === "number") {
      return sortOrder === "asc" ? valueA - valueB : valueB - valueA;
    }
  });

  const totalPages = Math.ceil((sortedLogs?.length || 0) / rowsPerPage);
  const paginatedLogs = sortedLogs?.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <section className="w-full">
      <Head>
        <title>{import.meta.env.ZUDOKU_PUBLIC_PAGE_TITLE} - Logs</title>
      </Head>
      <div className="py-4">
        <h1 className="text-2xl font-semibold">API Logs</h1>

        {!currentTenant && (
          <p className="text-muted-foreground">Please select a tenant to view API logs</p>
        )}

        {currentTenant && (
          <div className="mt-4 w-[90%] mx-auto border border-gray-300 rounded-md shadow-md p-6 sm:w-[95%] md:w-[90%] lg:w-[85%]">
            {isFetching && <p>Loading logs...</p>}
            {error && <p>Error: {error.message}</p>}
            {logs && (
              <>
                <div className="mb-4 flex justify-between items-center">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-[70%] px-4 py-2 border border-gray-300 rounded-md"
                  />
                  <div className="flex items-center">
                    <label htmlFor="rowsPerPage" className="mr-2 dark:text-white text-black">
                      Rows per page:
                    </label>
                    <select
                      id="rowsPerPage"
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      className="px-2 py-1 border border-gray-300 rounded-md bg-white text-black dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                <div className="overflow-auto max-h-[750px]">
                  <table className="table-auto w-full border-collapse text-sm">
                    <thead className="bg-muted text-left sticky top-0 z-10">
                    <tr>
                        <th
                          className="border border-gray-300 px-4 py-2 cursor-pointer"
                          onClick={() => handleSort("id")}
                        >
                          Id {sortColumn === "id" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="border border-gray-300 px-4 py-2 cursor-pointer"
                          onClick={() => handleSort("method")}
                        >
                          Method {sortColumn === "method" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="border border-gray-300 px-4 py-2 cursor-pointer"
                          onClick={() => handleSort("url")}
                        >
                          Url {sortColumn === "url" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="border border-gray-300 px-4 py-2 cursor-pointer"
                          onClick={() => handleSort("result")}
                        >
                          Result {sortColumn === "result" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="border border-gray-300 px-4 py-2 cursor-pointer"
                          onClick={() => handleSort("timestamp")}
                        >
                          Timestamp {sortColumn === "timestamp" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="border border-gray-300 px-4 py-2">Ip Address</th>
                        <th className="border border-gray-300 px-4 py-2">App Registration Id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedLogs.map((log) => {
                        let displayUrl = "";
                        try {
                          const url = new URL(log.url);
                          displayUrl = decodeURIComponent(`${url.pathname}${url.search}`);
                        } catch (error) {
                          displayUrl = " "; // Fallback value for invalid URLs
                        }

                        const badgeColor =
                        log.result >= 200 && log.result < 300
                          ? "bg-green-500 text-white dark:bg-green-400"
                          : log.result >= 400 && log.result < 600
                          ? "bg-red-500 text-white dark:bg-red-400"
                          : "bg-black text-white dark:bg-gray-700";

                        return (
                          <React.Fragment key={log.id}>
                            <tr
                              className="hover:bg-muted cursor-pointer"
                              onClick={() =>
                                setExpandedId(expandedId === log.id ? null : log.id)
                              }
                            >
                              <td className="border border-gray-300 px-4 py-2">{log.id}</td>
                              <td className="border border-gray-300 px-4 py-2">{log.method}</td>
                              <td
                                className="border border-gray-300 px-4 py-2 max-w-[200px] truncate"
                                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                title={log.url || "NULL"}
                              >
                                {displayUrl}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                <span
                                  className={`px-2 py-1 rounded ${badgeColor}`}
                                  title={
                                    (() => {
                                      try {
                                        const parsedError = JSON.parse(log.errorResponseBody);
                                        return `Result: ${log.result}\nError: ${JSON.stringify(parsedError, null, 2)}`;
                                      } catch {
                                        return `Result: ${log.result}\nError: ${log.errorResponseBody || "None"}`;
                                      }
                                    })()
                                  }
                                >
                                  {log.result}
                                </span>
                              </td>
                              <td className="border border-gray-300 px-4 py-2">{log.timestamp}</td>
                              <td className="border border-gray-300 px-4 py-2">
                                {log.ipAddress || "NULL"}
                              </td>
                              <td className="border border-gray-300 px-4 py-2 max-w-[150px] truncate"
                                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                title={log.appRegistrationId || "NULL"}
                              >
                                {log.appRegistrationId || "NULL"}
                              </td>
                            </tr>

                            {expandedId === log.id && (
                              <tr>
                                <td colSpan={9} className="bg-white dark:bg-black text-black dark:text-white px-4 py-4 border-t border-b">
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="break-words whitespace-normal"><strong>Request ID:</strong> {log.id}</div>
                                    <div className="break-words whitespace-normal"><strong>Method:</strong> {log.method}</div>
                                    <div className="break-words whitespace-normal"><strong>Full URL:</strong> {log.url}</div>
                                    <div className="break-words whitespace-normal"><strong>Result:</strong> {log.result}</div>
                                    <div className="break-words whitespace-normal"><strong>Timestamp:</strong> {log.timestamp}</div>
                                    <div className="break-words whitespace-normal"><strong>Error Response:</strong> {log.errorResponseBody || "NULL"}</div>
                                    <div className="break-words whitespace-normal"><strong>IP Address:</strong> {log.ipAddress || "NULL"}</div>
                                    <div className="break-words whitespace-normal"><strong>APIM Request ID:</strong> {log.apimRequestId}</div>
                                    <div className="break-words whitespace-normal"><strong>App Registration ID:</strong> {log.appRegistrationId || "NULL"}</div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-between items-center mt-4">
                  <button
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white text-black"
                    disabled={currentPage === 1}
                    onClick={handlePreviousPage}
                  >
                    Previous
                  </button>
                  <span className="dark:text-white text-black">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white text-black"
                    disabled={currentPage === totalPages}
                    onClick={handleNextPage}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}